/**
 * YumaSitoPage — console /yuma-sito (30/10/2026, Simone: «nella nostra console dovremo andare a salvare tutte queste
 * conversazioni»): le conversazioni dei visitatori di yumaos.com con Yuma, in SOLA LETTURA.
 *
 * In cima se Yuma è accesa e quanto spende (oggi contro il tetto del giorno, ultimi 30 giorni). Sotto, a computer,
 * l'elenco a sinistra e la conversazione scelta a destra, com'è stata vista dal visitatore; al telefono una cosa per
 * volta. `?c=<id>` apre direttamente una conversazione (ci arriva la scheda dell'iscritto).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, ButtonBase, Paper, Skeleton, Stack, ToggleButton, ToggleButtonGroup, Typography, alpha, useMediaQuery, useTheme } from '@mui/material';
import { ArrowBack, Refresh } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { creaContestoLocale } from '@gestionale/core/i18n/lingue';
import { formatData, formatValuta } from '@gestionale/core/i18n/formattazione';
import type { ConversazioneYumaPerLaConsole, RigaConversazioneYuma, SpesaDiYuma } from '@shared/yuma-sito/tipi';
import { VIOLA_NOTTE } from '@shared/yuma-sito/gatto';
import { tokens } from '../theme';
import { SoftChip } from '../components/SoftChip';
import { GattoPiccolo, Trascrizione } from '../components/yuma-sito/Trascrizione';
import { RiquadroSpesa } from '../components/yuma-sito/RiquadroSpesa';
import { leggiConversazione, leggiConversazioni } from '../services/yumaSitoService';

type Filtro = 'tutte' | 'con_modulo' | 'iscritti';

const passaIlFiltro = (r: RigaConversazioneYuma, f: Filtro) =>
  f === 'tutte' || (f === 'iscritti' ? !!r.lead_id : r.modulo_preparato || !!r.lead_id);

const messaggioDiErrore = (e: unknown) => (e instanceof Error ? e.message : String(e));

export const YumaSitoPage: React.FC = () => {
  const { t, i18n } = useTranslation('yumaSito');
  const tema = useTheme();
  const aComputer = useMediaQuery(tema.breakpoints.up('md'));
  const euro = useMemo(() => creaContestoLocale({ lingua: i18n.language, paese: 'IT', valuta: 'EUR', fusoOrario: 'Europe/Rome' }), [i18n.language]);
  const [parametri, setParametri] = useSearchParams();
  const scelta = parametri.get('c');

  const [righe, setRighe] = useState<RigaConversazioneYuma[] | null>(null);
  const [spesa, setSpesa] = useState<SpesaDiYuma | null>(null);
  const [altre, setAltre] = useState(false);
  const [filtro, setFiltro] = useState<Filtro>('tutte');
  const [errore, setErrore] = useState<string | null>(null);
  const [caricamento, setCaricamento] = useState(false);

  const carica = useCallback(async (primaDi?: string) => {
    setCaricamento(true);
    setErrore(null);
    try {
      const r = await leggiConversazioni(primaDi);
      setRighe((prima) => (primaDi && prima ? [...prima, ...r.conversazioni] : r.conversazioni));
      setAltre(r.altre);
      setSpesa(r.spesa);
    } catch (e) {
      setErrore(t('errori.elenco', { motivo: messaggioDiErrore(e) }));
    } finally {
      setCaricamento(false);
    }
  }, [t]);
  useEffect(() => void carica(), [carica]);

  const visibili = useMemo(() => (righe ?? []).filter((r) => passaIlFiltro(r, filtro)), [righe, filtro]);
  const scegli = (id: string | null) => {
    const p = new URLSearchParams(parametri);
    if (id) p.set('c', id);
    else p.delete('c');
    setParametri(p, { replace: !id });
  };

  const mostraElenco = aComputer || !scelta;
  const mostraDettaglio = aComputer || !!scelta;

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2} mb={3}>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h1" sx={{ fontFamily: tokens.font.display, fontWeight: 600, fontSize: { xs: '1.6rem', md: '1.95rem' }, lineHeight: 1.1, letterSpacing: '-0.02em', color: tokens.color.graphite }}>
            {t('titolo')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
            {t('sottotitolo')}
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={() => void carica()} disabled={caricamento} sx={{ minHeight: 40 }}>
          {t('aggiorna')}
        </Button>
      </Stack>

      {spesa && <RiquadroSpesa spesa={spesa} ctx={euro} onCambiata={setSpesa} />}

      {errore && <Alert severity="error" sx={{ mb: 2 }}>{errore}</Alert>}

      {!righe ? (
        <Stack spacing={2}>
          <Skeleton variant="rounded" height={90} />
          <Skeleton variant="rounded" height={320} />
        </Stack>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '380px minmax(0, 1fr)' }, gap: 2, alignItems: 'start' }}>
          {mostraElenco && (
            <Paper variant="outlined" sx={{ overflow: 'hidden', position: { md: 'sticky' }, top: { md: 16 } }}>
              <Box sx={{ p: 1.5, borderBottom: `1px solid ${tokens.color.line}` }}>
                <ToggleButtonGroup
                  value={filtro}
                  exclusive
                  size="small"
                  onChange={(_, v: Filtro | null) => v && setFiltro(v)}
                  aria-label={t('filtri.etichetta')}
                  sx={{ flexWrap: 'wrap' }}
                >
                  {(['tutte', 'con_modulo', 'iscritti'] as const).map((f) => (
                    <ToggleButton key={f} value={f} sx={{ textTransform: 'none', px: 1.5 }}>
                      {t(`filtri.${f}`)}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
              <Box sx={{ maxHeight: { md: 'calc(100vh - 300px)' }, overflowY: 'auto' }}>
                {visibili.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    {righe.length === 0 ? t('elenco.vuoto') : t('elenco.vuotoFiltro')}
                  </Typography>
                )}
                {visibili.map((r) => (
                  <RigaDellElenco key={r.id} riga={r} attiva={r.id === scelta} onScegli={() => scegli(r.id)} euro={euro} />
                ))}
                {altre && (
                  <Box sx={{ p: 1.5 }}>
                    <Button fullWidth variant="text" disabled={caricamento} onClick={() => void carica(righe[righe.length - 1]?.aggiornata_il)}>
                      {t('elenco.altre')}
                    </Button>
                  </Box>
                )}
              </Box>
            </Paper>
          )}

          {mostraDettaglio && <Dettaglio id={scelta} euro={euro} onIndietro={aComputer ? undefined : () => scegli(null)} />}
        </Box>
      )}
    </Box>
  );
};

const RigaDellElenco: React.FC<{ riga: RigaConversazioneYuma; attiva: boolean; onScegli: () => void; euro: ReturnType<typeof creaContestoLocale> }> = ({ riga, attiva, onScegli, euro }) => {
  const { t } = useTranslation('yumaSito');
  const chi = riga.chi.azienda ? `${riga.chi.nome ? `${riga.chi.nome}, ` : ''}${riga.chi.azienda}` : riga.chi.nome ?? riga.chi.email ?? t('elenco.senzaNome');
  return (
    <ButtonBase
      onClick={onScegli}
      aria-current={attiva ? 'true' : undefined}
      sx={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        px: 2,
        py: 1.5,
        borderBottom: `1px solid ${tokens.color.lineSoft}`,
        borderLeft: `3px solid ${attiva ? VIOLA_NOTTE : 'transparent'}`,
        bgcolor: attiva ? alpha(VIOLA_NOTTE, 0.05) : 'transparent',
        '&:hover': { bgcolor: alpha(VIOLA_NOTTE, 0.03) },
        '&:focus-visible': { outline: `2px solid ${VIOLA_NOTTE}`, outlineOffset: -2 },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'baseline' }}>
        <Typography sx={{ fontWeight: 650, fontSize: '0.9rem', color: tokens.color.graphite, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{chi}</Typography>
        <Typography sx={{ fontSize: '0.75rem', color: tokens.color.mist, whiteSpace: 'nowrap' }}>{formatData(riga.aggiornata_il, euro, 'giornoMeseBreve')}</Typography>
      </Box>
      <Typography
        sx={{ mt: 0.25, fontSize: '0.85rem', color: tokens.color.ash, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', overflowWrap: 'anywhere' }}
      >
        {riga.primo_messaggio}
      </Typography>
      <Box sx={{ mt: 0.75, display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
        {riga.lead_id ? <SoftChip tone="success" label={t('stato.iscritto')} /> : riga.modulo_preparato ? <SoftChip tone="info" label={t('stato.modulo')} /> : null}
        <Typography sx={{ fontSize: '0.75rem', color: tokens.color.mist }}>
          {t('elenco.messaggi', { count: riga.n_messaggi })}
          {', '}
          {riga.pagina_di_partenza ? t('elenco.daPagina', { pagina: riga.pagina_di_partenza }) : t('elenco.paginaDiYuma')}
          {riga.conto ? `, ${formatValuta(riga.conto.totale, euro)}` : ''}
        </Typography>
      </Box>
    </ButtonBase>
  );
};

const Dettaglio: React.FC<{ id: string | null; euro: ReturnType<typeof creaContestoLocale>; onIndietro?: () => void }> = ({ id, euro, onIndietro }) => {
  const { t } = useTranslation('yumaSito');
  const [conversazione, setConversazione] = useState<ConversazioneYumaPerLaConsole | null>(null);
  const [errore, setErrore] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let viva = true;
    setConversazione(null);
    setErrore(null);
    leggiConversazione(id)
      .then((c) => viva && setConversazione(c))
      .catch((e) => viva && setErrore(t('errori.conversazione', { motivo: messaggioDiErrore(e) })));
    return () => {
      viva = false;
    };
  }, [id, t]);

  if (!id) {
    return (
      <Paper variant="outlined" sx={{ p: 4, display: 'flex', alignItems: 'center', gap: 2, color: tokens.color.ash }}>
        <GattoPiccolo lato={36} />
        <Typography variant="body2">{t('dettaglio.scegli')}</Typography>
      </Paper>
    );
  }

  const c = conversazione;
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
      {onIndietro && (
        <Button startIcon={<ArrowBack />} onClick={onIndietro} sx={{ mb: 1.5, ml: -1 }}>
          {t('dettaglio.indietro')}
        </Button>
      )}
      {errore && <Alert severity="error">{errore}</Alert>}
      {!c && !errore && <Typography variant="body2" color="text.secondary">{t('dettaglio.caricamento')}</Typography>}
      {c && (
        <>
          <Box sx={{ pb: 2, mb: 2.5, borderBottom: `1px solid ${tokens.color.line}` }}>
            <Typography sx={{ fontFamily: tokens.font.display, fontWeight: 600, fontSize: '1.3rem', color: tokens.color.graphite, overflowWrap: 'anywhere' }}>
              {c.chi.azienda ?? c.chi.nome ?? c.chi.email ?? t('elenco.senzaNome')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {[
                t('dettaglio.iniziata', { data: formatData(c.creata_il, euro, 'dataOra') }),
                c.pagina_di_partenza ? t('elenco.daPagina', { pagina: c.pagina_di_partenza }) : t('elenco.paginaDiYuma'),
                t('dettaglio.costo', { costo: formatValuta(c.costo_usd, euro, { valuta: 'USD' }) }),
                c.scade_il ? t('dettaglio.scade', { data: formatData(c.scade_il, euro, 'media') }) : t('dettaglio.perSempre'),
              ].join('. ')}
            </Typography>
            {c.iscritto && (
              <Button component={RouterLink} to={`/aziende?lead=${encodeURIComponent(c.iscritto.id)}`} variant="outlined" size="small" sx={{ mt: 1.5 }}>
                {t('dettaglio.apriIscritto')}
              </Button>
            )}
            {c.raccolti.riassunto && (
              <Box sx={{ mt: 2, p: 1.5, borderRadius: `${tokens.radius.md}px`, bgcolor: tokens.color.bone }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: tokens.color.ash, mb: 0.5 }}>{t('dettaglio.riassunto')}</Typography>
                <Typography sx={{ fontSize: '0.875rem', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{c.raccolti.riassunto}</Typography>
              </Box>
            )}
          </Box>
          <Trascrizione messaggi={c.messaggi} ctx={euro} />
        </>
      )}
    </Paper>
  );
};
